import { InjectProperty } from '@jack-henry/frontend-utils/di';
import { AuditModel, Loan } from '@treasury/domain/loans';
import { UsersService } from '@treasury/domain/users';
import { LabeledList, TmBaseComponent } from '@treasury/presentation';
import '@treasury/presentation/components/tm-labeled-list';
import { format } from 'date-fns';
import { css, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';

export const tagName = 'loan-payment-readonly';
@customElement(tagName)
export class LoanPaymentReadonly extends TmBaseComponent {
    @property({ type: Object })
    public loan = new Loan();

    @property({ type: Boolean })
    public existingPayment = true;

    @property({ type: Boolean, reflect: true })
    public confirmation = false;

    @InjectProperty()
    declare private usersService: UsersService;

    @state()
    private auditOpen = false;

    @state()
    private approversOpen = false;

    protected render() {
        return [this.renderLoanDetails(), this.renderApprovalInformation()];
    }

    private toggleAudit() {
        this.auditOpen = !this.auditOpen;
    }

    private renderStatus() {
        if (!this.existingPayment) {
            return nothing;
        }
        if (!this.loan) {
            return nothing;
        }
        return html`<jhd-status .status=${this.loan.status} showLabel></jhd-status>`;
    }

    private renderApprovalInformation() {
        if (this.loan?.status !== 'PendingApproval' || !this.loan.updatedBy) {
            return nothing;
        }
        return html`<tm-section class="mt-3"
            ><treasury-pending-approval-template
                .approvalItem=${this.loan}
                class="pb-2"
                .approvals=${this.loan.completedApprovals}
                .eligibleApproversFunction=${async () =>
                    this.usersService.fetchAvailableApprovers({
                        approvalEntity: 'internalTransfer',
                        productId: this.loan.id as number | undefined,
                        updatedBy: parseInt(this.loan.updatedBy),
                    }) as unknown as string[]}
            ></treasury-pending-approval-template
        ></tm-section>`;
    }

    private renderAuditData() {
        if (!this.auditOpen) {
            return nothing;
        }
        const auditList = this.loan.audit.map(
            (auditItem: AuditModel) =>
                html`<div class="audit-item pb-2">
                    <span class="audit-date mr-1">${format(new Date(auditItem.date), 'Pp')}</span>
                    <span>${auditItem.createdBy} : ${auditItem.comments}</span>
                </div>`
        );
        return html`<div class="max-h-36 mt-6 pb-2">
            <div class="pb-2 font-medium">Audit</div>
            <div class="max-h-36 overflow-y-auto font-normal text-sm">${auditList}</div>
        </div>`;
    }

    private renderCreatedBy() {
        if (!this.loan.audit.length) {
            return nothing;
        }
        return html`<jhd-data-field
            .label=${'Created By'}
            .value=${this.loan.audit[this.loan.audit.length - 1].createdBy}
        ></jhd-data-field>`;
    }

    private renderAudit() {
        if (!this.loan.audit || this.confirmation) {
            return nothing;
        }
        const auditButtonText = this.auditOpen ? 'Hide Audit' : 'View Audit';
        return html`
            ${this.renderAuditData()}
            <tm-button class="mt-2" @click=${() => this.toggleAudit()}>
                ${auditButtonText}
            </tm-button>
        `;
    }

    private renderLoanDetails() {
        if (!this.loan) {
            return nothing;
        }
        return html`<div class="detail-wrapper px-6 py-3 bg-primary">
            <div class="loan-detail-wrapper flex items-center justify-between">
                <div class="detail-amount">
                    <div class="text-lg font-medium mb-1">Loan Payment</div>
                    <div class="text-2xl font-normal">
                        ${new Intl.NumberFormat('en-US', {
                            style: 'currency',
                            currency: 'USD',
                        }).format(this.loan.amount)}
                    </div>
                </div>
                <div class="detail-status flex items-center my-1 text-xs">
                    ${this.renderStatus()}
                </div>
            </div>

            <tm-labeled-list
                class="py-4"
                .list=${new LabeledList(
                    {
                        from: this.loan.fromAccount?.accountDisplayLabel,
                        to: this.loan.toAccount?.accountDisplayLabel,
                        divider: 'dashed',
                        type: this.loan.paymentOptionType,
                        frequency: 'One Time',
                        paymentDate: this.loan.transferDate
                            ? format(new Date(this.loan.transferDate || ''), 'P')
                            : undefined,
                        memo: this.loan.memo,
                    },
                    ['from', 'to', 'divider', 'type', 'frequency', 'paymentDate', 'memo'],
                    { to: 'To loan' }
                )}
            ></tm-labeled-list>
            ${this.existingPayment
                ? html`<tm-labeled-list
                          class="py-4"
                          .list=${new LabeledList(
                              {
                                  transactionId: this.loan.transactionId,
                                  creationDate: format(new Date(this.loan.createdDate || ''), 'P'),
                              },
                              ['transactionId', 'creationDate']
                          )}
                      ></tm-labeled-list>
                      ${this.renderCreatedBy()} ${this.renderAudit()}`
                : nothing}
        </div>`;
    }

    public static get styles() {
        return [
            css`
                .status svg {
                    display: inline-block;
                }
                .status {
                    color: var(--header-text-color);
                }
                .audit-item {
                    border-bottom: 1px dashed var(--border-color);
                    color: var(--header-text-color);
                }
                .detail-amount,
                .status {
                    color: var(--header-text-color);
                }
                .audit-date {
                    color: var(--secondary-text-color, #444444);
                }
                .max-h-36 {
                    max-height: 9rem;
                }
            `,
        ];
    }
}

declare global {
    interface HTMLElementTagNameMap {
        [tagName]: LoanPaymentReadonly;
    }
}
